import React from 'react';
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import ProtectedRoute from '../components/ProtectedRoute';
import RoleProtectedRoute from './RoleProtectedRoute';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';
import { useAuth } from '../contexts/AuthContext';
import AuthService from '../services/AuthService';
import Login from './Login/Login';
import ForgotPassword from './ForgotPassword/ForgotPassword';
import ResetPassword from './ResetPassword/ResetPassword';
import Dashboard from './Dashboard/Dashboard';
import JobList from './JobList/JobList';
import Admin from './Admin/Admin';
import StudiesList from './StudiesList';
import Audit from './Audit/Audit';
import Qc from './Qc/Qc';

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;

  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>One or more of the requested studies are not available at this time.</h4>
        {showStudyList && (
          <p className="mt-2">
            Return to the{' '}
            <Link
              className="text-primary-light"
              to="/submissions"
            >
              Submissions
            </Link>{' '}
            to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// Use our new Login component
const LoginComponent = Login;

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/login`,
    children: LoginComponent,
    private: false, // Make login route public
  },
  {
    path: `/forgotpassword`,
    children: ForgotPassword,
    private: false, // Make forgot password route public
  },
  {
    path: `/resetpassword`,
    children: ResetPassword,
    private: false, // Make reset password route public
  },
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
];

// NOT FOUND (404)
const notFoundRoute = { component: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];


  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  // Remove the conflicting WorkListRoute since we're using LoginRedirectRoute for '/'
  // const WorkListRoute = {
  //   path: '/',
  //   children: DataSourceWrapper,
  //   private: true,
  //   props: { children: WorkList, servicesManager, extensionManager },
  // };

  // Remove the LoginRedirectRoute and make worklist the default route
  // const LoginRedirectRoute = {
  //   path: '/',
  //   children: () => {
  //     console.log('LoginRedirectRoute - Rendering redirect to /login');
  //
  //     // Check if user is already authenticated
  //     const authService = AuthService.getInstance();
  //     const authState = authService.getAuthState();
  //
  //     console.log('LoginRedirectRoute - Auth state:', authState);
  //
  //     if (authState.isAuthenticated) {
  //       console.log('LoginRedirectRoute - User is authenticated, redirecting to /worklist');
  //       return <Navigate to="/worklist" replace />;
  //     }
  //
  //       console.log('LoginRedirectRoute - User is not authenticated, redirecting to /login');
  //       return (
  //         <Navigate
  //           to="/login"
  //           replace
  //         />
  //       );
  //   },
  //   private: false,
  // };

  // Protected study list route that handles /studiesList
  const ProtectedWorkListRoute = {
    path: '/studiesList',
    children: StudiesList,
    private: true,
  };

  // Dashboard route
  const DashboardRoute = {
    path: '/dashboard',
    children: Dashboard,
    private: true,
  };

  // Submissions route
  const JobListRoute = {
    path: '/submissions',
    children: JobList,
    private: true,
  };

  // Submission QC route
  const SubmissionQcRoute = {
    path: '/:submission_id/qc',
    children: Qc,
    private: true,
  };

  // Admin route with nested paths for specific admin sections
  const AdminRoute = {
    path: '/admin/*',
    children: props => (
      <RoleProtectedRoute allowedRoles={['admin']}>
        <Admin {...props} />
      </RoleProtectedRoute>
    ),
    private: true,
  };

  // Audit route
  const AuditRoute = {
    path: '/audit',
    children: Audit,
    private: true,
  };

  // Protocols route removed; accessible within Admin only

  // Root route that redirects to submissions (protected)
  const RootRedirectRoute = {
    path: '/',
    children: () => {
      return (
        <Navigate
          to="/submissions"
          replace
        />
      );
    },
    private: false,
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    // Add root redirect route FIRST to ensure it takes priority
    RootRedirectRoute,
    ...routes,
    // Add dashboard route
    DashboardRoute,
    // Add job list route
    JobListRoute,
    // Add submission QC route
    SubmissionQcRoute,
    // Add admin route
    AdminRoute,
    // Add audit route
    AuditRoute,
    // Add protected study list route
    ...(showStudyList ? [ProtectedWorkListRoute] : []),
    ...((customRoutes as any)?.routes || []),
    ...bakedInRoutes,
    (customRoutes as any)?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    history.navigate = useNavigate();


    try {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return (
        <ErrorBoundary context={`Route ${route.path}`}>
          <route.children
            {...rest}
            {...route.props}
            route={route}
            servicesManager={servicesManager}
            extensionManager={extensionManager}
            hotkeysManager={hotkeysManager}
          />
        </ErrorBoundary>
      );
    } catch (error) {
      return (
        <div className="flex h-full w-full items-center justify-center text-white">
          <div>
            <h4>Error loading route: {route.path}</h4>
            <p className="mt-2 text-sm text-red-400">{error.message}</p>
          </div>
        </div>
      );
    }
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <ProtectedRoute>
                <RouteWithErrorBoundary route={route} />
              </ProtectedRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
